<template>
  <tr
    class="bg-gray-100 text-sm text-right child:py-5 cursor-pointer hover:bg-gray-200/80 transition-colors"
    @click.stop="open = !open"
  >
    <td class="pl-8 text-left">
      <div class="flex items-center space-x-1">
        <button
          type="button"
          class="btn-icon btn-icon-default"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 transition-all duration-200"
            :class="[{ 'rotate-90': open }]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
        </button>

        <span>{{ group.label }}</span>
      </div>
    </td>

    <td class="pr-3">
      {{ australianCurrency(totals.budget) }}
    </td>

    <td class="bg-green-200/30">
      {{ australianCurrency(totals.currentClaim) }}
    </td>

    <td class="bg-green-200/30">
      {{ australianCurrency(totals.currentCertified) }}
    </td>

    <td
      class="bg-green-200/30"
      :class="{ 'text-red-600': totals.variance !== 0 }"
    >
      {{ australianCurrency(totals.variance) }}
    </td>

    <td class="bg-green-200/30">
      {{ australianCurrency(totals.previous) }}
    </td>

    <td class="bg-green-200/30 pr-3">
      {{ australianCurrency(totals.cumulative) }}
    </td>

    <td class="bg-blue-100/30">
      <span v-if="showPercentages">
        {{ totals.budget > 0 ? australianNumber((totals.currentClaim / totals.budget) * 100) : 0 }}%
      </span>
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/30"
    >
      {{ totals.budget > 0 ? australianNumber((totals.currentCertified / totals.budget) * 100) : 0 }}%
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/30"
    >
      {{ totals.budget > 0 ? australianNumber((totals.previous / totals.budget) * 100) : 0 }}%
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/30 pr-3"
    >
      {{ totals.budget > 0 ? australianNumber((totals.cumulative / totals.budget) * 100) : 0 }}%
    </td>

    <td class="pr-3">
      {{ australianCurrency(totals.budget - totals.cumulative) }}
    </td>
  </tr>

  <template v-if="open">
    <VariationOrderCostRow
      v-for="cost in costs"
      :key="cost.id"
      :claim="claim"
      :variation-order-cost="cost"
      :variation-order="cost.variation_order"
      :show-percentages="showPercentages"
    />
  </template>
</template>

<script setup lang="ts">
import { australianCurrency, australianNumber } from '@/helpers';
import Claim from '@/models/Claim';
import VariationOrder from '@/models/VariationOrder';
import VariationOrderCost from '@/models/VariationOrderCost';
import { computed, ref } from 'vue';
import VariationOrderCostRow from './VariationOrderCostRow.vue';

export type VariationOrderCategoryGroup = {
  key: string;
  label: string;
};

const props = defineProps<{
  group: VariationOrderCategoryGroup;
  variationOrders: VariationOrder[];
  costs: VariationOrderCost[];
  claim: Claim;
  showPercentages?: boolean;
}>();

const open = ref(false);

const totals = computed(() => {
  const result = {
    budget: 0,
    currentClaim: 0,
    currentCertified: 0,
    previous: 0,
    cumulative: 0,
    variance: 0,
  };

  props.variationOrders.forEach((variationOrder) => {
    result.budget += variationOrder.budget;
    result.previous += variationOrder.previous(props.claim);

    const variationOrderCost = variationOrder.costs.find((cost) => {
      return cost.claim_id === props.claim.id;
    }) ?? variationOrder.costs[0];

    if(variationOrderCost) {
      result.currentClaim += variationOrderCost.current_claim;
      result.currentCertified += variationOrderCost.current_certified;
      result.variance += variationOrderCost.current_claim - variationOrderCost.current_certified;
    }
  });

  result.cumulative = result.previous + result.currentCertified;

  return result;
});
</script>
